<template>
  <div
    class="max-w-screen-md transition-opacity"
    :class="{ 'opacity-0 pointer-events-none': !loaded }"
  >
    <div
      class="flex flex-wrap justify-center text-brown space-x-3 lg:space-x-5 items-baseline"
    >
      <span
        class="hexagon leading-none p-2 lg:p-3 font-title text-1-mob lg:text-2-desk cursor-pointer select-none uppercase text-center"
        :class="[filterBgClass(id), { 'opacity-30': opacity(id) }]"
        ref="polygon"
        v-for="({ id, type, name }, i) in departments"
        @click="handleFilterClick(id, type)"
        :key="i"
        v-text="name"
      />
    </div>
    <div
      class="flex flex-wrap justify-center space-x-3 lg:space-x-5 space-y-3 lg:space-y-5 items-baseline"
    >
      <span
        class="border border-brown rounded-full leading-none p-2 lg:p-3 font-title text-1-mob lg:text-2-desk cursor-pointer select-none uppercase"
        :class="{ 'opacity-30': opacity(id) }"
        v-for="({ id, type, name }, i) in categories"
        @click="handleFilterClick(id, type)"
        :key="i"
        v-text="name"
        :style="filterBgColor(id)"
      />
    </div>
    <div
      class="flex flex-wrap justify-center leading-none space-x-3 lg:space-x-5 space-y-3 lg:space-y-5 items-baseline"
    >
      <span
        class="border border-brown p-2 lg:p-3 font-title text-1-mob lg:text-2-desk cursor-pointer select-none uppercase"
        :class="{ 'opacity-30': opacity(id) }"
        v-for="({ id, type, name }, i) in continents"
        @click="handleFilterClick(id, type)"
        :key="i"
        v-text="name"
        :style="filterBgColor(id)"
      />
    </div>
    <div class="w-full mt-5 lg:mt-8 flex justify-center">
      <span
        class="leading-none select-none font-title text-1-mob lg:text-2-desk"
        :class="[opacity() ? 'opacity-30' : 'cursor-pointer']"
        @click="handleClearAll"
        >CLEAR ALL FILTERS ×</span
      >
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { colors } from "@utils/tailwind";
import { globals } from "@io/global";
import { get as filterCategory } from "@io/filterCategory";

const highlightColorKeys = [
  "egg",
  "pink",
  "blue",
  "powder",
  "rust",
  "red",
  "yellow",
  "stone"
];

export default {
  name: "BlockFilter",

  props: {
    backgroundColor: {
      type: String,
      default: "avo"
    }
  },

  data: () => ({
    ready: false
  }),

  computed: {
    globals,
    filterCategory,
    ...mapState({
      active: state => state.directory.active,
      isMobile: state => state.ui.isMobile
    }),
    departments() {
      if (!this.globals) return [];
      return this.globals.filterArrangement.department.filter(
        category => category.type === "department"
      );
    },
    categories() {
      if (!this.globals) return [];
      return this.globals.filterArrangement.category.filter(
        category => category.type === "category"
      );
    },
    continents() {
      if (!this.filterCategory) return [];
      return Object.values(this.filterCategory)
        .filter(category => category.type === "continent")
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    loaded() {
      if (
        !this.ready ||
        this.departments.length === 0 ||
        this.categories.length === 0 ||
        this.continents.length === 0
      )
        return false;

      return true;
    }
  },

  // This is a fx for Safari as the outer container not being pixel perfectly
  // wide causes misalignment with the inner border component throwing off the
  // shape
  watch: {
    loaded() {
      this.safariWidthFix();
    },
    isMobile() {
      this.safariWidthFix();
    }
  },

  async mounted() {
    // Time it takes for bg transition to complete
    await new Promise(resolve => setTimeout(() => resolve(), 250));
    this.ready = true;
  },

  methods: {
    async safariWidthFix() {
      await this.$nextTick();

      // remove any prev set widths
      this.$refs.polygon.forEach(polygon => {
        polygon.style.width = `unset`;
      });

      // set new widths
      this.$refs.polygon.forEach(polygon => {
        polygon.style.width = `${polygon.offsetWidth}px`;
      });
    },
    handleFilterClick(id, type) {
      const val = { id, type, colorIndex: this.randomIndex() };
      const set = val.id in this.$store.state.directory.active;
      this.$store.commit(set ? "deleteActive" : "setActive", val);
    },
    handleClearAll() {
      this.$store.commit("clearActive");
    },
    randomIndex() {
      return Math.floor(Math.random() * Math.floor(highlightColorKeys.length));
    },
    filterBgColor(id) {
      if (!(id in this.active)) return colors[this.backgroundColor];
      return {
        backgroundColor: colors[highlightColorKeys[this.active[id].colorIndex]]
      };
    },
    filterBgClass(id) {
      if (!(id in this.active)) return this.backgroundColor;
      return highlightColorKeys[this.active[id].colorIndex];
    },
    opacity(id = false) {
      // Handle no filters selected
      if (Object.keys(this.active).length === 0) {
        // Handle the clear all
        if (!id) return true;

        return false;
      }

      // Handle the clear all
      if (!id) return false;

      // Handle filter is selected
      if (id in this.active) return false;

      // Handle group is selected
      const { type } = this.filterCategory[id];
      const index = Object.keys(this.active).findIndex(
        activeId => this.filterCategory[activeId].type === type
      );
      return index === -1 ? false : true;
    }
  }
};
</script>

<style scoped>
.hexagon {
  position: relative;
  background: theme(colors.brown);
  clip-path: polygon(
    5px 0px,
    calc(100% - 5px) 0px,
    100% 50%,
    calc(100% - 5px) 100%,
    5px 100%,
    0px 50%
  );
}

.hexagon:before {
  position: absolute;
  content: "";
  height: calc(100% - 2px); /* 100% - 2 * border width */
  width: calc(100% - 2px); /* 100% - 2 * border width */
  left: 1px; /* border width */
  top: 1px; /* border width */
  clip-path: polygon(
    4.5px 0px,
    calc(100% - 4.5px) 0px,
    100% 50%,
    calc(100% - 4.5px) 100%,
    4.5px 100%,
    0px 50%
  );
  z-index: -1;
}

.hexagon.white:before {
  background: theme(colors.white);
}
.hexagon.avo:before {
  background: theme(colors.avo);
}
.hexagon.egg:before {
  background: theme(colors.egg);
}
.hexagon.pink:before {
  background: theme(colors.pink);
}
.hexagon.blue:before {
  background: theme(colors.blue);
}
.hexagon.powder:before {
  background: theme(colors.powder);
}
.hexagon.rust:before {
  background: theme(colors.rust);
}
.hexagon.red:before {
  background: theme(colors.red);
}
.hexagon.yellow:before {
  background: theme(colors.yellow);
}
.hexagon.stone:before {
  background: theme(colors.stone);
}
</style>
